<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Event, PlayerProfile, AttendanceStatus } from '@/types/models';
import AppModal from '@/components/ui/AppModal.vue';
import AppBadge from '@/components/ui/AppBadge.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton } from 'naive-ui';
import { Check, X, HelpCircle, Users, CheckCheck, RotateCcw } from 'lucide-vue-next';

interface Props {
  modelValue: boolean;
  event: Event | null;
  players: PlayerProfile[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
}>();

const { t } = useI18n();
const { notifySuccess } = useToastNotification();

// Map playerId -> AttendanceStatus
const attendanceMap = ref<Record<string, AttendanceStatus>>({
  ply_1: 'ATTENDING',
  ply_2: 'ATTENDING',
  ply_3: 'ATTENDING',
  ply_4: 'ATTENDING',
  ply_5: 'DECLINED',
  ply_6: 'ATTENDING',
  ply_7: 'PENDING',
});

const setStatus = (playerId: string, status: AttendanceStatus) => {
  attendanceMap.value[playerId] = status;
};

const markAllAttending = () => {
  props.players.forEach(p => {
    attendanceMap.value[p.id] = 'ATTENDING';
  });
};

const resetAll = () => {
  props.players.forEach(p => {
    attendanceMap.value[p.id] = 'PENDING';
  });
};

const counts = computed(() => {
  const result = { attending: 0, declined: 0, pending: 0 };
  props.players.forEach(p => {
    const s = attendanceMap.value[p.id] || 'PENDING';
    if (s === 'ATTENDING') result.attending++;
    else if (s === 'DECLINED') result.declined++;
    else result.pending++;
  });
  return result;
});

const handleSave = () => {
  notifySuccess(t('attendanceModal.successTitle'), t('attendanceModal.successMsg', { title: props.event?.title || '' }));
  emit('update:modelValue', false);
};
</script>

<template>
  <AppModal
    :model-value="modelValue"
    @update:model-value="(val) => emit('update:modelValue', val)"
    :title="t('attendanceModal.title')"
    :subtitle="event ? `${event.title} - ${event.location}` : ''"
    max-width="xl"
  >
    <div v-if="event" class="space-y-5">
      <!-- Attendance summary counts -->
      <div class="grid grid-cols-3 gap-3">
        <div class="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
          <span class="text-[10px] uppercase font-bold text-emerald-400 tracking-wider">{{ t('events.attending') }}</span>
          <span class="text-xl font-extrabold text-emerald-300 block mt-0.5">{{ counts.attending }}</span>
        </div>
        <div class="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-center">
          <span class="text-[10px] uppercase font-bold text-rose-400 tracking-wider">{{ t('events.declined') }}</span>
          <span class="text-xl font-extrabold text-rose-300 block mt-0.5">{{ counts.declined }}</span>
        </div>
        <div class="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
          <span class="text-[10px] uppercase font-bold text-slate-400 tracking-wider">{{ t('events.pending') }}</span>
          <span class="text-xl font-extrabold text-slate-300 block mt-0.5">{{ counts.pending }}</span>
        </div>
      </div>

      <!-- Quick Batch Actions -->
      <div class="flex items-center justify-between gap-2 pt-1">
        <span class="text-xs text-slate-400 font-medium">{{ players.length }} {{ t('teams.players') }}</span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            @click="markAllAttending"
            class="text-xs font-semibold text-sky-400 hover:text-sky-300 flex items-center gap-1 transition"
          >
            <CheckCheck class="w-3.5 h-3.5" />
            <span>{{ t('attendanceModal.markAllAttending') }}</span>
          </button>
          <span class="text-slate-600">&bull;</span>
          <button
            type="button"
            @click="resetAll"
            class="text-xs font-semibold text-slate-400 hover:text-slate-200 flex items-center gap-1 transition"
          >
            <RotateCcw class="w-3.5 h-3.5" />
            <span>{{ t('attendanceModal.resetAll') }}</span>
          </button>
        </div>
      </div>

      <!-- Player list check-in -->
      <div class="max-h-80 overflow-y-auto space-y-2 pr-1">
        <div
          v-for="player in players"
          :key="player.id"
          class="p-3 rounded-xl bg-slate-950 border border-slate-800/80 flex items-center justify-between gap-3"
        >
          <div class="flex items-center gap-3">
            <span class="font-mono font-bold text-xs text-sky-400 w-8">#{{ player.jerseyNumber }}</span>
            <div>
              <p class="font-bold text-xs text-slate-200">{{ player.firstName }} {{ player.lastName }}</p>
              <p class="text-[10px] text-slate-500 font-mono">{{ player.position }}</p>
            </div>
          </div>

          <div class="flex items-center gap-1.5">
            <button
              type="button"
              @click="setStatus(player.id, 'ATTENDING')"
              :class="[
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 shadow-sm',
                (attendanceMap[player.id] || 'PENDING') === 'ATTENDING'
                  ? 'bg-emerald-500 text-slate-950 font-bold ring-2 ring-emerald-400/30'
                  : 'bg-slate-900 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              ]"
            >
              <Check class="w-3.5 h-3.5" />
              <span>{{ t('attendanceModal.in') }}</span>
            </button>

            <button
              type="button"
              @click="setStatus(player.id, 'DECLINED')"
              :class="[
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 shadow-sm',
                (attendanceMap[player.id] || 'PENDING') === 'DECLINED'
                  ? 'bg-rose-500 text-white font-bold ring-2 ring-rose-400/30'
                  : 'bg-slate-900 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              ]"
            >
              <X class="w-3.5 h-3.5" />
              <span>{{ t('attendanceModal.out') }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="pt-4 flex justify-end gap-3 border-t border-slate-800">
        <NButton
          quaternary
          @click="emit('update:modelValue', false)"
        >
          {{ t('attendanceModal.cancel') }}
        </NButton>
        <NButton
          type="primary"
          @click="handleSave"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          {{ t('attendanceModal.saveAttendance') }}
        </NButton>
      </div>
    </div>
  </AppModal>
</template>